import { useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import html2pdf from 'html2pdf.js';
import { api } from '../lib/api';
import { listReturnUrl } from '../lib/listReturn';
import { useClients, useCrms } from '../lib/lookups';

/**
 * A customer purchase order as entered, on one A4 page: to print, or to save
 * as a PDF - the same toolbar and letterhead as the job card.
 */

interface PoPrint {
  id: string; poNumber: string; poDate: string; poDeliveryDate: string; orderType: string;
  jobName: string; client: string; artwork: string; quantity: string; pricePerBox: string;
  amount: string; vat: string; finalAmount: string; materialNo: string; description: string;
  productId: string; crmUserId: string; awMaterialNo?: string; dispatchAddress?: string;
  isCancelled?: boolean; cancelReason?: string; jobCards?: number; jobCardedQty?: number;
  createdOn?: string | null;
}

const esc = (v: unknown) => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const num = (v: unknown) => { const n = Number(String(v ?? '').replace(/,/g, '')); return Number.isFinite(n) ? n : null; };
const qtyText = (v: unknown) => { const n = num(v); return n == null ? esc(v) : n.toLocaleString('en-IN'); };
const money = (v: unknown) => { const n = num(v); return n == null ? esc(v) : `₹ ${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; };
const dateText = (v: string | null | undefined) => {
  if (!v) return '';
  const d = new Date(v.length === 10 ? `${v}T00:00:00` : v);
  return Number.isNaN(d.getTime()) ? esc(v) : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

export function PurchaseOrderPrintPage() {
  const { id = '' } = useParams();
  const decoded = decodeURIComponent(id);
  const printRef = useRef<HTMLDivElement>(null);
  const [downloading, setDownloading] = useState(false);
  const q = useQuery({
    queryKey: ['po-print', decoded],
    queryFn: () => api<PoPrint>(`/api/purchase-orders/${encodeURIComponent(decoded)}`),
  });
  const clients = useClients();
  const crms = useCrms();

  async function downloadPdf() {
    const el = printRef.current;
    if (!el || downloading || !q.data) return;
    setDownloading(true);
    try {
      await html2pdf().set({
        margin: 0,
        filename: `Purchase-Order-${(q.data.poNumber || decoded).replace(/[\\/:*?"<>|]+/g, '-')}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      } as never).from(el).save();
    } finally {
      setDownloading(false);
    }
  }

  if (q.isLoading) return <div className="p-6 text-slate-500">Loading purchase order…</div>;
  if (q.isError || !q.data) {
    return (
      <div className="p-6">
        <p className="text-red-600">Could not load the purchase order: {(q.error as Error)?.message ?? 'unknown'}</p>
        <Link to={listReturnUrl('/purchase-orders')} className="text-brand underline text-sm">← Back to Purchase Orders</Link>
      </div>
    );
  }
  const r = q.data;
  const clientName = clients.data?.find((c) => c.id === r.client)?.name ?? r.client;
  const crmName = crms.data?.find((c) => String(c.id) === String(r.crmUserId))?.name ?? '';

  const th = (t: string) => `<td style="padding:5px 10px 5px 0;font-weight:800;font-size:11px;width:22%;vertical-align:top;">${t}</td>`;
  const td = (v: string) => `<td style="padding:5px 10px 5px 0;font-size:11.5px;width:28%;vertical-align:top;">${v || '&nbsp;'}</td>`;
  const row = (cells: string[]) => `<tr>${cells.join('')}</tr>`;
  const section = (t: string) => `<div style="margin:12px 0 4px;font-size:12px;font-weight:900;text-transform:uppercase;letter-spacing:0.08em;border-bottom:2px solid #000;padding-bottom:2px;">${t}</div>`;
  const table = (rows: string) => `<table style="width:100%;border-collapse:collapse;">${rows}</table>`;

  const html = `
    <div style="border:2px solid #000;padding:6px 10px;margin-bottom:8px;text-align:center;">
      <div style="font-size:16px;font-weight:900;letter-spacing:0.05em;">VASUDEV PRINT PACK PVT. LTD.</div>
      <div style="font-size:8.5px;font-weight:600;">Hansa Industrial Park, Derabassi-Barwala Rd, Dera Bassi, Punjab 140507 · Phone: 085807 03745 · mis@vasudevprintpack.com</div>
      <div style="margin-top:2px;font-size:12px;font-weight:900;text-transform:uppercase;letter-spacing:0.15em;">Purchase Order</div>
    </div>
    ${r.isCancelled ? `<div style="margin-bottom:8px;padding:6px 10px;border:2px solid #b91c1c;color:#b91c1c;font-weight:900;font-size:12px;">CANCELLED${r.cancelReason ? ` — ${esc(r.cancelReason)}` : ''}</div>` : ''}
    ${section('Order')}
    ${table(
      row([th('PO No.'), td(esc(r.poNumber)), th('PO Date'), td(dateText(r.poDate))])
      + row([th('Our Ref'), td(esc(r.id)), th('Order Type'), td(esc(r.orderType))])
      + row([th('Client'), td(esc(clientName)), th('CRM'), td(esc(crmName))])
      + row([th('Requested Delivery'), td(dateText(r.poDeliveryDate)), th('Job Cards'), td(r.jobCards ? `${r.jobCards} (${qtyText(r.jobCardedQty)} boxes)` : 'None yet')])
    )}
    ${section('Product')}
    ${table(
      row([th('Product / Job Name'), `<td colspan="3" style="padding:5px 0;font-size:12px;font-weight:700;">${esc(r.jobName)}</td>`])
      + row([th('Product Code'), td(esc(r.productId)), th('Item Code'), td(esc(r.materialNo))])
      + row([th('Artwork / Design No'), td(esc(r.artwork)), th('A/W Material No'), td(esc(r.awMaterialNo))])
    )}
    ${section('Quantity &amp; Amount')}
    ${table(
      row([th('Quantity (boxes)'), td(qtyText(r.quantity)), th('Price per Box'), td(money(r.pricePerBox))])
      + row([th('Gross Amount'), td(money(r.amount)), th('GST %'), td(esc(r.vat))])
      + row([th('Final Amount (incl. GST)'), `<td colspan="3" style="padding:5px 0;font-size:13px;font-weight:900;">${money(r.finalAmount)}</td>`])
    )}
    ${r.dispatchAddress ? `${section('Dispatch Address')}<div style="font-size:11.5px;white-space:pre-wrap;">${esc(r.dispatchAddress)}</div>` : ''}
    ${r.description ? `${section('Product Description')}<div style="font-size:11.5px;white-space:pre-wrap;">${esc(r.description)}</div>` : ''}
    <div style="margin-top:40px;display:flex;justify-content:space-between;font-size:10.5px;font-weight:700;">
      <div style="border-top:1px solid #000;padding-top:3px;width:30%;text-align:center;">Prepared by</div>
      <div style="border-top:1px solid #000;padding-top:3px;width:30%;text-align:center;">Checked by</div>
      <div style="border-top:1px solid #000;padding-top:3px;width:30%;text-align:center;">Authorised Signatory</div>
    </div>
    <div style="margin-top:14px;font-size:8.5px;color:#555;text-align:right;">Printed ${new Date().toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>`;

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: `
        @media print {
          .no-print { display: none !important; }
          body { margin: 0 !important; padding: 0 !important; }
          @page { margin: 0; size: A4; }
        }
      ` }} />
      <div className="no-print" style={{ position: 'sticky', top: 0, zIndex: 10, background: '#fff', borderBottom: '1px solid #e2e8f0', padding: '10px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontFamily: 'Arial, sans-serif' }}>
        <Link to={listReturnUrl('/purchase-orders')} style={{ fontSize: '13px', color: '#475569', textDecoration: 'none' }}>← Back to Purchase Orders</Link>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={() => window.print()}
            style={{ padding: '8px 20px', borderRadius: '6px', border: '1px solid #cbd5e1', background: '#fff', color: '#1e293b', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
            Print
          </button>
          <button onClick={() => void downloadPdf()} disabled={downloading}
            style={{ padding: '8px 20px', borderRadius: '6px', border: 'none', background: '#014868', color: '#fff', fontSize: '13px', fontWeight: 600, cursor: downloading ? 'not-allowed' : 'pointer', opacity: downloading ? 0.7 : 1 }}>
            {downloading ? 'Generating…' : 'Save PDF'}
          </button>
        </div>
      </div>
      <div
        ref={printRef}
        style={{ width: '210mm', margin: '0 auto', padding: '12mm 15mm', boxSizing: 'border-box', background: '#fff', color: '#000', lineHeight: '1.4', fontFamily: 'Arial, Helvetica, sans-serif' }}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </>
  );
}
